/**
 * QuoteCache - Client-side caching utility for quote details
 *
 * This utility provides caching functionality for quote data to reduce
 * redundant API calls and improve performance. It stores quote details
 * in memory with a configurable timeout.
 */

class QuoteCache {
  static cache = new Map();
  static cacheTimeout = 5 * 60 * 1000; // 5 minutes

  /**
   * Get a cached quote by ID
   * @param {number} id - The quote ID
   * @returns {Object|null} - The cached quote data or null if not found/expired
   */
  static get(id) {
    const cached = this.cache.get(id);
    if (!cached) {
      return null;
    }

    // Check if cache has expired
    if (Date.now() - cached.timestamp > this.cacheTimeout) {
      this.cache.delete(id);
      return null;
    }

    return cached.data;
  }

  /**
   * Store quote data in cache
   * @param {number} id - The quote ID
   * @param {Object} data - The quote data to cache
   */
  static set(id, data) {
    this.cache.set(id, {
      data,
      timestamp: Date.now(),
    });
  }

  /**
   * Remove a specific quote from cache
   * @param {number} id - The quote ID to remove
   */
  static remove(id) {
    this.cache.delete(id);
  }

  /**
   * Clear all cached data
   */
  static clear() {
    this.cache.clear();
  }

  /**
   * Get cache statistics
   * @returns {Object} - Cache statistics
   */
  static getStats() {
    const now = Date.now();
    let validEntries = 0;
    let expiredEntries = 0;

    for (const [, cached] of this.cache.entries()) {
      if (now - cached.timestamp > this.cacheTimeout) {
        expiredEntries++;
      } else {
        validEntries++;
      }
    }

    return {
      totalEntries: this.cache.size,
      validEntries,
      expiredEntries,
      cacheTimeout: this.cacheTimeout,
    };
  }

  /**
   * Clean up expired entries
   */
  static cleanup() {
    const now = Date.now();
    for (const [id, cached] of this.cache.entries()) {
      if (now - cached.timestamp > this.cacheTimeout) {
        this.cache.delete(id);
      }
    }
  }
}

export default QuoteCache;
